/**
 * Helpers for the "edited" indicator on posts and post replies.
 *
 * Mirrors the writes that iOS and Android already do today:
 *   - Android: `ZoeyRepository.editPost(...)` (`:780`) and
 *     `ZoeyRepository.editPostReply(...)` (`:804`) both stamp
 *     `edited_at` with `Timestamp.now()`.
 *   - iOS: `PostView.swift:248-252` and `PostDetailView.swift:55-60`
 *     render an inline "(edited)" / "· edited" affix when
 *     `edited_at != nil`.
 *
 * The web app already round-trips the document via `updateDocument(...)`
 * for the post edit flow but, prior to Wave O.3, never stamped the
 * timestamp. Centralizing it here means every caller that mutates the
 * user-visible body of a post or reply just composes their updates
 * through `markEdited` and stays in sync with the other clients.
 */

import { Timestamp } from "firebase/firestore";
import type { HouseholdPost, HouseholdPostReply } from "@/lib/models";

/**
 * Wrap a Firestore `update`-shaped object so it also stamps `edited_at`
 * to "now". The caller's own field updates win — if `edited_at` is
 * already present (e.g. an explicit override or a no-op pass-through)
 * we keep their value so the helper is idempotent.
 *
 * Pure & DOM-free so it can be unit-tested without Firebase.
 */
export function markEdited(
  updates: Record<string, unknown>,
): Record<string, unknown> {
  if (Object.prototype.hasOwnProperty.call(updates, "edited_at")) {
    return { ...updates };
  }
  return {
    ...updates,
    edited_at: Timestamp.now(),
  };
}

/**
 * Returns true when a post or reply has a non-null `edited_at`. Used by
 * PostCard / PostDetail to decide whether to render the "(edited)"
 * affix.
 */
export function isEdited(
  post: Pick<HouseholdPost, "edited_at"> | Pick<HouseholdPostReply, "edited_at">,
): boolean {
  return post.edited_at != null;
}
